import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../auth/AuthContext';

// ============ DOC TYPE CONFIG ============

const CATEGORIES = [
  { prefix: 'A', label: 'A - Deal Summaries' },
  { prefix: 'B', label: 'B - Borrower / Sponsor' },
  { prefix: 'C', label: 'C - Credit / Background' },
  { prefix: 'D', label: 'D - Subject Property' },
  { prefix: 'E', label: 'E - Entity' },
  { prefix: 'F', label: 'F - Final Loan Docs' },
  { prefix: 'L', label: 'L - Lender Docs' },
  { prefix: 'M', label: 'M - Misc / Brokerage' },
  { prefix: 'X', label: 'X - X-Files (Admin only)' },
];

const CATEGORY_STRING = CATEGORIES.reduce((acc, c) => { acc[c.prefix] = c.label; return acc; }, {});

const OWNER_TYPES = ['Personal', 'Business', 'Both'];

const EXTRACTABLE_DOC_TYPES = new Set([
  'Bank Statement',
  'Balance Sheet',
  'Business Debt Schedule',
  'Loan Statement',
  'Credit Report',
  '1040 Tax Return',
  '1120-S Tax Return',
  '1065 Tax Return',
  '1120 Tax Return',
  'W-2',
  'Pay Stub',
  '1099',
  'Appraisal',
  'BPO (Broker Price Opinion)',
  'CMA (Comparative Market Analysis)',
  'PFS (Personal Financial Statement)',
  'Brokerage Statement',
  'SREO (Schedule of Real Estate Owned)',
  'VOE (Verification of Employment)',
  'K-1',
  'Rent Roll',
  'Operating Statement (T-12)',
  'Purchase Contract',
  'Mortgage Statement',
  'HELOC Statement',
  'Payoff Statement',
  'Construction Budget',
  'Scope of Work',
  'Lease',
  'Business P&L',
  'Personal P&L',
  'Tax Bill',
  'Inspection Report',
  'A-B Contract (Seller to Wholesaler)',
  'B-C Contract (Wholesaler to Buyer)',
  'B-C Contract (Wholesaler to Next Wholesaler)',
  'C-D Contract (Wholesaler to Buyer)',
  'Assignment',
  'Term Sheet',
  // future doc types will be added here as their backend prompts come online
]);

// dropdownLabel (Airtable key) -> { prefix, filenameLabel, fields }
const DOC_TYPES = {
  // A - Deal Summaries
  "Deal Summary": { prefix: 'A', filenameLabel: "Deal Summary", fields: [{ key: "docDate", label: "Document date", type: "date", required: true }] },
  "Executive Summary": { prefix: 'A', filenameLabel: "Exec Summary", fields: [{ key: "docDate", label: "Document date", type: "date", required: true }] },
  "Pre-Approval Letter": { prefix: 'A', filenameLabel: "Pre-Approval", fields: [{ key: "docDate", label: "Document date", type: "date", required: true }] },
  "Engagement Letter": { prefix: 'A', filenameLabel: "Engagement Letter", fields: [{ key: "docDate", label: "Date signed", type: "date", required: true }] },
  "Pitch Deck": { prefix: 'A', filenameLabel: "Pitch Deck", fields: [{ key: "docDate", label: "Document date", type: "date", required: true }] },
  "Property Listing": { prefix: 'A', filenameLabel: "Listing", fields: [{ key: "docDate", label: "Document date", type: "date", required: true }] },
  // B - Borrower / Sponsor
  "Loan Application": { prefix: 'B', filenameLabel: "Loan App", fields: [{ key: "docDate", label: "Document date", type: "date", required: true }] },
  "PFS (Personal Financial Statement)": { prefix: 'B', filenameLabel: "PFS", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Bank Statement": { prefix: 'B', filenameLabel: "Bank Stmt", fields: [{ key: "institution", label: "Institution", type: "text", required: true }, { key: "accountLast4", label: "Account last 4", type: "number", required: true }, { key: "ownerName", label: "Account holder name", type: "text", required: true }, { key: "docDate", label: "Statement end date", type: "date", required: true }] },
  "Brokerage Statement": { prefix: 'B', filenameLabel: "Brokerage Stmt", fields: [{ key: "institution", label: "Institution", type: "text", required: true }, { key: "accountLast4", label: "Account last 4", type: "number", required: true }, { key: "ownerName", label: "Account holder name", type: "text", required: true }, { key: "docDate", label: "Statement end date", type: "date", required: true }] },
  "Mortgage Statement": { prefix: 'B', filenameLabel: "Mortgage Stmt", fields: [{ key: "institution", label: "Institution", type: "text", required: true }, { key: "accountLast4", label: "Account last 4", type: "number", required: true }, { key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "ownerName", label: "Account holder name", type: "text", required: true }, { key: "docDate", label: "Statement date", type: "date", required: true }] },
  "1040 Tax Return": { prefix: 'B', filenameLabel: "1040 Tax Return", fields: [{ key: "ownerName", label: "Taxpayer name", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "1120 Tax Return": { prefix: 'B', filenameLabel: "1120 Tax Return", fields: [{ key: "ownerName", label: "Entity name", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "1120-S Tax Return": { prefix: 'B', filenameLabel: "1120-S Tax Return", fields: [{ key: "ownerName", label: "Entity name", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "1065 Tax Return": { prefix: 'B', filenameLabel: "1065 Tax Return", fields: [{ key: "ownerName", label: "Entity name", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "K-1": { prefix: 'B', filenameLabel: "K-1", fields: [{ key: "ownerName", label: "Recipient name", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "W-2": { prefix: 'B', filenameLabel: "W-2", fields: [{ key: "ownerName", label: "Employee name", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "1099": { prefix: 'B', filenameLabel: "1099", fields: [{ key: "ownerName", label: "Recipient name", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "Pay Stub": { prefix: 'B', filenameLabel: "Pay Stub", fields: [{ key: "ownerName", label: "Employee name", type: "text", required: true }, { key: "docDate", label: "Pay period end date", type: "date", required: true }] },
  "VOE (Verification of Employment)": { prefix: 'B', filenameLabel: "VOE", fields: [{ key: "ownerName", label: "Employee name", type: "text", required: true }, { key: "docDate", label: "Verification date", type: "date", required: true }] },
  "Gift Letter": { prefix: 'B', filenameLabel: "Gift Letter", fields: [{ key: "donorName", label: "Donor name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "HELOC Statement": { prefix: 'B', filenameLabel: "HELOC Stmt", fields: [{ key: "institution", label: "Institution", type: "text", required: true }, { key: "accountLast4", label: "Account last 4", type: "number", required: true }, { key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "ownerName", label: "Account holder name", type: "text", required: true }, { key: "docDate", label: "Statement date", type: "date", required: true }] },
  "Loan Statement": { prefix: 'B', filenameLabel: "Loan Stmt", fields: [{ key: "loanKind", label: "Loan kind", type: "select", required: true, options: ["Auto", "Boat", "RV", "Personal loan", "Student", "Business", "Other"] }, { key: "institution", label: "Institution", type: "text", required: true }, { key: "accountLast4", label: "Account last 4", type: "number", required: true }, { key: "ownerName", label: "Account holder name", type: "text", required: true }, { key: "docDate", label: "Statement date", type: "date", required: true }] },
  "SREO (Schedule of Real Estate Owned)": { prefix: 'B', filenameLabel: "SREO", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Business P&L": { prefix: 'B', filenameLabel: "Business P&L", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Personal P&L": { prefix: 'B', filenameLabel: "Personal P&L", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Balance Sheet": { prefix: 'B', filenameLabel: "Balance Sheet", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Business Debt Schedule": { prefix: 'B', filenameLabel: "Debt Schedule", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Driver's License": { prefix: 'B', filenameLabel: "DL", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "expiresOn", label: "Expiration date", type: "date", required: true }] },
  "Photo ID": { prefix: 'B', filenameLabel: "Photo ID", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }] },
  "Member ID": { prefix: 'B', filenameLabel: "Member ID", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }] },
  "Credit Authorization": { prefix: 'B', filenameLabel: "Credit Auth", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Letter of Explanation": { prefix: 'B', filenameLabel: "LOE", fields: [{ key: "topic", label: "Topic / subject", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Resume / Track Record": { prefix: 'B', filenameLabel: "Resume", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Trust Documents": { prefix: 'B', filenameLabel: "Trust Docs", fields: [{ key: "trustName", label: "Trust name", type: "text", required: true }, { key: "docDate", label: "Effective date", type: "date", required: true }] },
  // C - Credit / Background
  "Credit Report": { prefix: 'C', filenameLabel: "Credit Report", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "docDate", label: "Pull date", type: "date", required: true }] },
  "Background Check": { prefix: 'C', filenameLabel: "Background Check", fields: [{ key: "ownerName", label: "Subject name", type: "text", required: true }, { key: "docDate", label: "Report date", type: "date", required: true }] },
  "Bankruptcy Discharge": { prefix: 'C', filenameLabel: "BK Discharge", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "docDate", label: "Discharge date", type: "date", required: true }] },
  "Foreclosure Resolution": { prefix: 'C', filenameLabel: "FC Resolution", fields: [{ key: "ownerName", label: "Borrower name", type: "text", required: true }, { key: "docDate", label: "Resolution date", type: "date", required: true }] },
  // D - Subject Property
  "Purchase Contract": { prefix: 'D', filenameLabel: "Purchase Contract", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "A-B Contract (Seller to Wholesaler)": { prefix: 'D', filenameLabel: "A-B Contract", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "B-C Contract (Wholesaler to Buyer)": { prefix: 'D', filenameLabel: "B-C Contract (Buyer)", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "B-C Contract (Wholesaler to Next Wholesaler)": { prefix: 'D', filenameLabel: "B-C Contract (Next WS)", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "C-D Contract (Wholesaler to Buyer)": { prefix: 'D', filenameLabel: "C-D Contract", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Assignment": { prefix: 'D', filenameLabel: "Assignment", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Property Profile": { prefix: 'D', filenameLabel: "Prop Profile", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Prelim Title": { prefix: 'D', filenameLabel: "Prelim Title", fields: [{ key: "titleCompany", label: "Title company", type: "text", required: true }, { key: "docDate", label: "Title date", type: "date", required: true }] },
  "Title Commitment": { prefix: 'D', filenameLabel: "Title Commitment", fields: [{ key: "titleCompany", label: "Title company", type: "text", required: true }, { key: "docDate", label: "Commitment date", type: "date", required: true }] },
  "Appraisal": { prefix: 'D', filenameLabel: "Appraisal", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "appraiserName", label: "Appraiser firm", type: "text", required: true }, { key: "docDate", label: "Appraisal date", type: "date", required: true }] },
  "BPO (Broker Price Opinion)": { prefix: 'D', filenameLabel: "BPO", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "brokerName", label: "Broker / firm", type: "text", required: true }, { key: "docDate", label: "BPO date", type: "date", required: true }] },
  "CMA (Comparative Market Analysis)": { prefix: 'D', filenameLabel: "CMA", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "brokerName", label: "Broker / firm", type: "text", required: true }, { key: "docDate", label: "CMA date", type: "date", required: true }] },
  "Comparables": { prefix: 'D', filenameLabel: "Comps", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Inspection Report": { prefix: 'D', filenameLabel: "Inspection", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "inspectorName", label: "Inspector firm", type: "text", required: true }, { key: "docDate", label: "Inspection date", type: "date", required: true }] },
  "Property Photos": { prefix: 'D', filenameLabel: "Photos", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Survey": { prefix: 'D', filenameLabel: "Survey", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "ALTA Survey": { prefix: 'D', filenameLabel: "ALTA Survey", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Plat Map": { prefix: 'D', filenameLabel: "Plat Map", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Zoning Report": { prefix: 'D', filenameLabel: "Zoning", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Environmental Report": { prefix: 'D', filenameLabel: "Environmental", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Phase I Environmental": { prefix: 'D', filenameLabel: "Phase I Enviro", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Phase II Environmental": { prefix: 'D', filenameLabel: "Phase II Enviro", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "PCOR (Preliminary Change of Ownership Report)": { prefix: 'D', filenameLabel: "PCOR", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Rent Roll": { prefix: 'D', filenameLabel: "Rent Roll", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Rent Roll Certification": { prefix: 'D', filenameLabel: "Rent Roll Cert", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Lease": { prefix: 'D', filenameLabel: "Lease", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "tenantName", label: "Tenant name", type: "text", required: true }, { key: "docDate", label: "Lease start date", type: "date", required: true }] },
  "Tenant Estoppel": { prefix: 'D', filenameLabel: "Estoppel", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "tenantName", label: "Tenant name", type: "text", required: true }, { key: "docDate", label: "Estoppel date", type: "date", required: true }] },
  "Operating Statement (T-12)": { prefix: 'D', filenameLabel: "T-12", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Property P&L": { prefix: 'D', filenameLabel: "Property P&L", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "As-of date", type: "date", required: true }] },
  "Tax Bill": { prefix: 'D', filenameLabel: "Tax Bill", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "Tax Roll": { prefix: 'D', filenameLabel: "Tax Roll", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "taxYear", label: "Tax year", type: "year", required: true }] },
  "Payoff Statement": { prefix: 'D', filenameLabel: "Payoff Stmt", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Payoff date", type: "date", required: true }] },
  "HOA Docs": { prefix: 'D', filenameLabel: "HOA Docs", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "hoaKind", label: "Doc kind", type: "select", required: true, options: ["CC&Rs / Declaration", "Bylaws", "Budget", "Meeting Minutes", "Master Insurance", "Other"] }, { key: "hoaName", label: "HOA name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "HOA Cert": { prefix: 'D', filenameLabel: "HOA Cert", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "hoaName", label: "HOA name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Proof of Insurance": { prefix: 'D', filenameLabel: "Proof of Insurance", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "carrier", label: "Insurance carrier", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Insurance Binder": { prefix: 'D', filenameLabel: "Insurance Binder", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "carrier", label: "Insurance carrier", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Insurance Quote": { prefix: 'D', filenameLabel: "Insurance Quote", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "carrier", label: "Insurance carrier", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Insurance Receipt": { prefix: 'D', filenameLabel: "Insurance Receipt", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "carrier", label: "Insurance carrier", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Insurance Invoice": { prefix: 'D', filenameLabel: "Insurance Invoice", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "carrier", label: "Insurance carrier", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Flood Disclosure": { prefix: 'D', filenameLabel: "Flood Disclosure", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Scope of Work": { prefix: 'D', filenameLabel: "SOW", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Construction Budget": { prefix: 'D', filenameLabel: "Construction Budget", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Permits": { prefix: 'D', filenameLabel: "Permits", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Repair Receipts": { prefix: 'D', filenameLabel: "Repair Receipts", fields: [{ key: "propertyAddress", label: "Property street address", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  // E - Entity
  "AOO (Articles of Organization)": { prefix: 'E', filenameLabel: "AOO", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Filing date", type: "date", required: true }] },
  "AOI (Articles of Incorporation)": { prefix: 'E', filenameLabel: "AOI", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Filing date", type: "date", required: true }] },
  "OA (Operating Agreement)": { prefix: 'E', filenameLabel: "OA", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Effective date", type: "date", required: true }] },
  "Bylaws": { prefix: 'E', filenameLabel: "Bylaws", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Effective date", type: "date", required: true }] },
  "Membership Agreement": { prefix: 'E', filenameLabel: "Membership Agreement", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Effective date", type: "date", required: true }] },
  "Shareholder Agreement": { prefix: 'E', filenameLabel: "Shareholder Agreement", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Effective date", type: "date", required: true }] },
  "Certificate of Good Standing": { prefix: 'E', filenameLabel: "Good Standing", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Issue date", type: "date", required: true }] },
  "Annual Report": { prefix: 'E', filenameLabel: "Annual Report", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Filing date", type: "date", required: true }] },
  "EIN (EIN Letter)": { prefix: 'E', filenameLabel: "EIN", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }] },
  "Resolutions": { prefix: 'E', filenameLabel: "Resolutions", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Resolution date", type: "date", required: true }] },
  "SOI (Statement of Information)": { prefix: 'E', filenameLabel: "SOI", fields: [{ key: "entityName", label: "Entity name", type: "text", required: true }, { key: "docDate", label: "Filing date", type: "date", required: true }] },
  // F - Final Loan Docs
  "Signed Note": { prefix: 'F', filenameLabel: "Signed Note", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Note date", type: "date", required: true }] },
  "Signed DOT (Deed of Trust)": { prefix: 'F', filenameLabel: "Signed DOT", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Recording date", type: "date", required: true }] },
  "Personal Guaranty": { prefix: 'F', filenameLabel: "Personal Guaranty", fields: [{ key: "guarantorName", label: "Guarantor name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Loan Agreement": { prefix: 'F', filenameLabel: "Loan Agreement", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Agreement date", type: "date", required: true }] },
  "Final CD (Closing Disclosure)": { prefix: 'F', filenameLabel: "Final CD", fields: [{ key: "docDate", label: "Closing date", type: "date", required: true }] },
  "Recorded Documents": { prefix: 'F', filenameLabel: "Recorded Docs", fields: [{ key: "docTypeLabel", label: "Document type", type: "text", required: true }, { key: "docDate", label: "Recording date", type: "date", required: true }] },
  "Wire Confirmation": { prefix: 'F', filenameLabel: "Wire Confirmation", fields: [{ key: "docDate", label: "Wire date", type: "date", required: true }] },
  "Title Policy": { prefix: 'F', filenameLabel: "Title Policy", fields: [{ key: "titleCompany", label: "Title company", type: "text", required: true }, { key: "docDate", label: "Policy date", type: "date", required: true }] },
  "CPL (Closing Protection Letter)": { prefix: 'F', filenameLabel: "CPL", fields: [{ key: "titleCompany", label: "Title company", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Subordination": { prefix: 'F', filenameLabel: "Subordination", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "SNDA (Subordination, Non-Disturbance & Attornment)": { prefix: 'F', filenameLabel: "SNDA", fields: [{ key: "tenantName", label: "Tenant name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  // L - Lender Docs
  "LOI (Letter of Intent)": { prefix: 'L', filenameLabel: "LOI", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Term Sheet": { prefix: 'L', filenameLabel: "Term Sheet", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Conditional Commitment": { prefix: 'L', filenameLabel: "Conditional Commitment", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Rate Lock": { prefix: 'L', filenameLabel: "Rate Lock", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Lock date", type: "date", required: true }] },
  "Lender Submission Package": { prefix: 'L', filenameLabel: "Submission Pkg", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Submission date", type: "date", required: true }] },
  "Withdrawn LOI": { prefix: 'L', filenameLabel: "Withdrawn LOI", fields: [{ key: "lenderName", label: "Lender name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Capital Partner Agreement": { prefix: 'L', filenameLabel: "Capital Partner", fields: [{ key: "partnerName", label: "Partner / entity name", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  // M - Misc / Brokerage
  "Internal Notes": { prefix: 'M', filenameLabel: "Internal Notes", fields: [{ key: "topic", label: "Topic / subject", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
  "Broker Commission Agreement": { prefix: 'M', filenameLabel: "Commission Agreement", fields: [{ key: "partyName", label: "Counterparty", type: "text", required: true }, { key: "docDate", label: "Agreement date", type: "date", required: true }] },
  "Commission Statement": { prefix: 'M', filenameLabel: "Commission Stmt", fields: [{ key: "docDate", label: "Statement date", type: "date", required: true }] },
  "Referral Fee Agreement": { prefix: 'M', filenameLabel: "Referral Fee", fields: [{ key: "partyName", label: "Referral party", type: "text", required: true }, { key: "docDate", label: "Agreement date", type: "date", required: true }] },
  "Misc": { prefix: 'M', filenameLabel: "Misc", fields: [{ key: "topic", label: "Description", type: "text", required: true }, { key: "docDate", label: "Document date", type: "date", required: true }] },
};

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'docx', 'xlsx'];
const MAX_FILE_SIZE = 50 * 1024 * 1024;
const STATUS_OPTIONS = ['Requested', 'Received', 'Under review', 'Approved', 'Needs replacement'];

// ============ STYLES ============

const s = {
  card:           { background:'#fff', border:'1px solid #C9D9EE', borderRadius:'14px', overflow:'hidden', marginBottom:'14px', boxShadow:'0 1px 2px rgba(20,60,120,0.04)' },
  cardHead:       { background:'#D0E0F5', borderBottom:'1px solid #BCD2EC', padding:'0 20px', minHeight:'40px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px' },
  cardTitle:      { color:'#154D97', fontSize:'12px', fontWeight:700, letterSpacing:'0.04em', textTransform:'uppercase', fontFamily:"'Inter',sans-serif" },
  collapseBtn:    { background:'none', border:'none', color:'rgba(255,255,255,0.7)', cursor:'pointer', fontSize:'14px', padding:0 },
  cardBody:       { padding:'16px' },
  subLabel:       { fontSize:'10px', fontWeight:700, color:'#4A7FBF', letterSpacing:'0.07em', textTransform:'uppercase', marginBottom:'10px' },
  subHint:        { fontSize:'11px', color:'#8a9ab5', marginBottom:'8px', fontStyle:'italic' },
  emptyText:      { fontSize:'13px', color:'#8a9ab5' },
  divider:        { borderTop:'1px solid #E4EDF8', margin:'14px 0' },
  toolbar:        { display:'flex', alignItems:'center', justifyContent:'flex-end', gap:'10px', flexWrap:'wrap', marginBottom:'4px' },
  archiveToggle:  { display:'inline-flex', width:'fit-content', alignItems:'center', gap:'6px', fontSize:'12px', color:'#8A94A6', cursor:'pointer', userSelect:'none', marginRight:'auto' },
  uploadBtn:      { display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', background:'#154D97', color:'#fff', border:'none', borderRadius:'9px', fontSize:'12.5px', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  dropZone:       (dragOver) => ({
    border: dragOver ? '2px dashed #154D97' : '2px dashed #B8D4F0',
    background: dragOver ? '#E4EDF8' : '#F5F9FE',
    borderRadius:'10px', padding:'24px 16px', textAlign:'center', color:'#4A7FBF',
    fontSize:'13px', cursor:'pointer', transition:'all 0.15s', marginBottom:'12px',
  }),
  dropZoneTitle:  { fontSize:'14px', fontWeight:600, color:'#154D97', marginBottom:'4px' },
  dropZoneHint:   { fontSize:'11px', color:'#8a9ab5' },
  catGroup:       { marginBottom:'8px' },
  catHeader:      {
                    display:'flex', alignItems:'center', gap:'10px', cursor:'pointer',
                    margin:'14px 0 6px', fontFamily:"'Inter',sans-serif", fontSize:'11px', fontWeight:700,
                    letterSpacing:'0.05em', textTransform:'uppercase', color:'#154D97',
                    background:'#E4EDF8', border:'1px solid #D9E5F4', borderRadius:'10px', padding:'8px 13px',
                  },
  catHeaderLeft:  { display:'flex', alignItems:'center', gap:'8px' },
  catHeaderRule:  { flex:1 },
  catHeaderCount: { fontSize:'10px', fontWeight:600, color:'#154D97', background:'#fff', border:'1px solid #C9D9EE', padding:'1px 8px', borderRadius:'10px' },
  colHeadRow:     { display:'grid', gridTemplateColumns:'1fr 120px 70px', gap:'12px', padding:'0 6px 4px', marginTop:'4px' },
  colHead:        { fontFamily:"'Inter',sans-serif", fontSize:'9.5px', fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', color:'#A7B3C4' },
  colHeadR:       { fontFamily:"'Inter',sans-serif", fontSize:'9.5px', fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', color:'#A7B3C4', textAlign:'right' },
  docRow:         { display:'grid', gridTemplateColumns:'1fr 120px 70px', gap:'12px', alignItems:'start', padding:'9px 6px', background:'#fff', borderBottom:'1px solid #F2F5F9', marginBottom:0 },
  docRowArchived: { display:'grid', gridTemplateColumns:'1fr 120px 70px', gap:'12px', alignItems:'start', padding:'9px 6px', background:'#FBFBF7', borderBottom:'1px solid #F2F5F9', marginBottom:0, opacity:0.7 },
  statusCol:      { display:'flex', alignItems:'flex-start', paddingTop:'1px' },
  docName:        { fontSize:'13px', color:'#0D2545', fontWeight:500, wordBreak:'break-word' },
  docMeta:        { display:'flex', gap:'8px', alignItems:'center', marginTop:'4px', flexWrap:'wrap' },
  docMetaText:    { fontSize:'11px', color:'#8a9ab5' },
  condPill:       { fontSize:'10px', fontWeight:600, padding:'2px 7px', borderRadius:'4px', background:'#FDF0DC', color:'#7A4A08', whiteSpace:'nowrap' },
  aiPanel:        { marginTop:'6px', background:'#F8FAFD', border:'1px solid #E4EDF8', borderRadius:'6px', padding:'8px 10px' },
  aiSummary:      { fontSize:'11px', color:'#4A4A6B', fontStyle:'italic', lineHeight:1.45, paddingBottom:'2px' },
  flagBlock:      { fontSize:'11px', lineHeight:1.5, whiteSpace:'pre-wrap', color:'#4A4A6B', marginTop:'4px', paddingTop:'4px', borderTop:'1px solid #E4EDF8' },
  flagBlockNoTop: { fontSize:'11px', lineHeight:1.5, whiteSpace:'pre-wrap', color:'#4A4A6B' },
  flagToggle:     { marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'6px', cursor:'pointer', userSelect:'none', fontSize:'11px', fontWeight:600, color:'#B5651D', background:'#FDF7EE', border:'1px solid #F5D49A', borderRadius:'5px', padding:'3px 8px' },
  flagToggleClean:{ marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'6px', cursor:'pointer', userSelect:'none', fontSize:'11px', fontWeight:600, color:'#6b7280', background:'#F5F5F0', border:'1px solid #DDD', borderRadius:'5px', padding:'3px 8px' },
  flagHeadReview: { fontWeight:700, color:'#B5651D', display:'block', marginTop:'4px' },
  flagHeadAware:  { fontWeight:700, color:'#1A5FA8', display:'block', marginTop:'4px' },
  flagHeadNotes:  { fontWeight:700, color:'#6b7280', display:'block', marginTop:'4px' },
  docActions:     { display:'flex', alignItems:'center', gap:'2px', justifyContent:'flex-end' },
  iconBtn:        { background:'transparent', border:'none', borderRadius:'6px', padding:'5px', color:'#8A94A6', cursor:'pointer', fontSize:'12px', fontFamily:'inherit', display:'flex', alignItems:'center', gap:'4px' },
  iconBtnAi:      { background:'transparent', border:'none', borderRadius:'6px', padding:'5px 7px', color:'#5E3B9C', cursor:'pointer', fontSize:'12px', fontFamily:'inherit', display:'flex', alignItems:'center', gap:'4px' },
  iconBtnDanger:  { background:'transparent', border:'none', borderRadius:'6px', padding:'5px', color:'#B08968', cursor:'pointer', fontSize:'12px', fontFamily:'inherit', display:'flex', alignItems:'center', gap:'4px' },
  iconBtnBusy:    { background:'#E4EDF8', border:'none', borderRadius:'6px', padding:'5px 7px', color:'#8A94A6', cursor:'wait', fontSize:'12px', fontFamily:'inherit', display:'flex', alignItems:'center', gap:'4px' },
  statusSelect:   { padding:'4px 8px', border:'1px solid #C9D9EE', borderRadius:'7px', fontSize:'11px', color:'#2E4763', fontFamily:'inherit', cursor:'pointer', background:'#fff' },
  aiDot:          (kind) => {
    const c = {
      flag:    { bg:'#FBF7F3', color:'#B08968', bd:'#EFE2D2' },
      clean:   { bg:'#EFF3F7', color:'#4A6885', bd:'#E1E8EF' },
      fail:    { bg:'#FBEFEF', color:'#A6544E', bd:'#EFD9D7' },
      busy:    { bg:'#E4EDF8', color:'#154D97', bd:'#D4E2F1' },
      pending: { bg:'#F4F6F9', color:'#A7B3C4', bd:'#E6EAF0' },
    }[kind] || { bg:'#F4F6F9', color:'#A7B3C4', bd:'#E6EAF0' };
    return { flexShrink:0, width:'20px', height:'16px', borderRadius:'5px', background:c.bg, color:c.color, border:`1px solid ${c.bd}`, fontSize:'8px', fontWeight:700, letterSpacing:'0.02em', display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', userSelect:'none' };
  },
  viewBtn:        { background:'transparent', border:'none', borderRadius:'6px', padding:'5px', color:'#8A94A6', cursor:'pointer', fontSize:'15px', display:'flex', alignItems:'center', textDecoration:'none' },
  viewBtnDim:     { background:'transparent', border:'none', borderRadius:'6px', padding:'5px', color:'#CBD3DE', cursor:'default', fontSize:'15px', display:'flex', alignItems:'center' },
  menuWrap:       { position:'relative', display:'flex' },
  menuBtn:        { background:'transparent', border:'none', borderRadius:'6px', padding:'5px', color:'#8A94A6', cursor:'pointer', fontSize:'16px', display:'flex', alignItems:'center' },
  menuBackdrop:   { position:'fixed', inset:0, zIndex:200 },
  menu:           { position:'absolute', top:'28px', right:0, zIndex:201, background:'#fff', border:'1px solid #E6EAF0', borderRadius:'10px', boxShadow:'0 8px 24px rgba(20,60,120,0.14)', padding:'6px', minWidth:'188px' },
  menuLabel:      { fontFamily:"'Inter',sans-serif", fontSize:'9px', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'#A7B3C4', padding:'6px 9px 3px' },
  menuItem:       { display:'flex', alignItems:'center', gap:'9px', width:'100%', textAlign:'left', background:'transparent', border:'none', borderRadius:'7px', padding:'7px 9px', fontSize:'12.5px', color:'#33415A', cursor:'pointer', fontFamily:'inherit' },
  menuItemDanger: { display:'flex', alignItems:'center', gap:'9px', width:'100%', textAlign:'left', background:'transparent', border:'none', borderRadius:'7px', padding:'7px 9px', fontSize:'12.5px', color:'#B08968', cursor:'pointer', fontFamily:'inherit' },
  menuStatusItem: (on) => ({ display:'flex', alignItems:'center', gap:'8px', width:'100%', textAlign:'left', background: on ? '#E4EDF8' : 'transparent', border:'none', borderRadius:'7px', padding:'6px 9px', fontSize:'12.5px', color: on ? '#154D97' : '#5a6b82', fontWeight: on ? 600 : 400, cursor:'pointer', fontFamily:'inherit' }),
  menuDivider:    { height:'1px', background:'#EEF1F5', margin:'5px 4px' },
  modalOverlay:   { position:'fixed', inset:0, background:'rgba(13, 37, 69, 0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'20px' },
  modal:          { background:'#fff', borderRadius:'10px', width:'100%', maxWidth:'560px', maxHeight:'90vh', overflow:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' },
  modalHead:      { background:'#4A7FBF', padding:'12px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'2px solid #7AADE0' },
  modalTitle:     { color:'#fff', fontSize:'13px', fontWeight:700, letterSpacing:'0.04em' },
  modalClose:     { background:'none', border:'none', color:'rgba(255,255,255,0.8)', cursor:'pointer', fontSize:'18px', padding:0 },
  modalBody:      { padding:'18px' },
  fieldGrid:      { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'12px', marginBottom:'12px' },
  field:          { display:'flex', flexDirection:'column', gap:'3px' },
  fieldLabel:     { fontSize:'10px', fontWeight:700, color:'#4A7FBF', letterSpacing:'0.07em', textTransform:'uppercase' },
  fieldReq:       { color:'#c0392b', marginLeft:'2px' },
  input:          { padding:'7px 10px', border:'1.5px solid #B8D4F0', borderRadius:'6px', fontSize:'13px', color:'#0D2545', fontFamily:'inherit', outline:'none', width:'100%', boxSizing:'border-box' },
  select:         { padding:'7px 10px', border:'1.5px solid #B8D4F0', borderRadius:'6px', fontSize:'13px', color:'#0D2545', fontFamily:'inherit', outline:'none', width:'100%', cursor:'pointer', background:'#fff', boxSizing:'border-box' },
  filePreview:    { background:'#E4EDF8', border:'1px solid #B8D4F0', borderRadius:'6px', padding:'10px 12px', marginBottom:'12px', fontSize:'12px', color:'#154D97', display:'flex', alignItems:'center', gap:'8px' },
  filenamePreview:{ background:'#F5F9FE', border:'1px dashed #B8D4F0', borderRadius:'6px', padding:'10px 12px', marginBottom:'12px', fontSize:'12px', color:'#0D3565', fontFamily:'monospace', wordBreak:'break-all' },
  condBox:        { background:'#FDF7EE', border:'1px solid #F5D49A', borderRadius:'7px', padding:'10px 12px', marginTop:'14px' },
  condToggle:     { display:'inline-flex', width:'fit-content', alignItems:'center', gap:'8px', fontSize:'13px', color:'#7A4A08', cursor:'pointer', userSelect:'none', fontWeight:600 },
  progressBar:    { width:'100%', height:'6px', background:'#E4EDF8', borderRadius:'3px', overflow:'hidden', margin:'8px 0' },
  progressFill:   (pct) => ({ height:'100%', width:`${pct}%`, background:'#154D97', transition:'width 0.2s' }),
  modalActions:   { display:'flex', alignItems:'center', justifyContent:'flex-end', gap:'8px', marginTop:'16px', paddingTop:'14px', borderTop:'1px solid #E4EDF8' },
  cancelBtn:      { padding:'8px 16px', background:'transparent', color:'#8a9ab5', border:'1.5px solid #B8D4F0', borderRadius:'7px', fontSize:'13px', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  primaryBtn:     { padding:'8px 20px', background:'#154D97', color:'#fff', border:'none', borderRadius:'7px', fontSize:'13px', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  errorMsg:       { fontSize:'12px', color:'#c0392b', marginTop:'6px' },
  statusPill:     (status) => {
    const colors = {
      'Requested':         { bg:'#FAEEDA', color:'#8a5a12' },
      'Received':          { bg:'#E4EDF8', color:'#154D97' },
      'Under review':      { bg:'#F0F5FB', color:'#1A5FA8' },
      'Approved':          { bg:'#EAF3DE', color:'#3B6D11' },
      'Needs replacement': { bg:'#FBEFEF', color:'#A23A2E' },
    };
    const c = colors[status] || { bg:'#F1EFE8', color:'#444441' };
    return { fontSize:'11px', fontWeight:600, padding:'3px 10px', borderRadius:'16px', background:c.bg, color:c.color, whiteSpace:'nowrap' };
  },
  aiPill:         (aiStatus) => {
    const colors = {
      'Not started':        { bg:'#F1EFE8', color:'#666666' },
      'Extracting':         { bg:'#EAE9FE', color:'#4A40A0' },
      'Reviewed - clean':   { bg:'#D4EDDA', color:'#155724' },
      'Reviewed - flagged': { bg:'#FDF0DC', color:'#7A4A08' },
      'Failed':             { bg:'#F8D7DA', color:'#721C24' },
      'Not applicable':     { bg:'#F1EFE8', color:'#999999' },
    };
    const c = colors[aiStatus] || { bg:'#F1EFE8', color:'#999' };
    return { fontSize:'10px', fontWeight:600, padding:'2px 7px', borderRadius:'4px', background:c.bg, color:c.color, whiteSpace:'nowrap' };
  },
};

// ============ HELPERS ============

function fileExtension(filename) {
  if (!filename || !filename.includes('.')) return '';
  return filename.split('.').pop().toLowerCase();
}

function formatDateMMDDYY(dateStr) {
  if (!dateStr) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return dateStr;
  return `${m[2]}.${m[3]}.${m[1].slice(2)}`;
}

const NAME_ONLY_STATEMENTS = new Set(['Bank Statement','Brokerage Statement','Mortgage Statement','HELOC Statement','Loan Statement']);

function buildPreviewFilename(docTypeKey, fields, ext, forcePrefix) {
  const def = DOC_TYPES[docTypeKey];
  if (!def) return '';
  const prefix = forcePrefix || def.prefix;
  const parts = [prefix, def.filenameLabel];
  const skipOwnerType = NAME_ONLY_STATEMENTS.has(docTypeKey);
  const keys = def.fields.map(f => f.key);
  const used = {};
  for (const fld of def.fields) {
    if (fld.key === 'docDate') continue;
    if (fld.key === 'ownerType' && skipOwnerType) continue;
    if (used[fld.key]) continue;
    if (fld.key === 'institution' && keys.includes('accountLast4')) {
      const inst = fields.institution || '';
      const last4 = fields.accountLast4 || '';
      const combo = `${inst} ${last4}`.trim();
      if (combo) parts.push(combo);
      used.institution = true; used.accountLast4 = true;
      continue;
    }
    if (fld.key === 'accountLast4' && keys.includes('institution')) continue;
    const v = fields[fld.key];
    if (v) parts.push(String(v));
    used[fld.key] = true;
  }
  if (keys.includes('docDate') && fields.docDate) {
    parts.push(formatDateMMDDYY(fields.docDate));
  }
  return parts.filter(Boolean).join(' - ') + (ext ? `.${ext}` : '');
}

// Title-cases a name that arrived in ALL CAPS (QuickBooks headers etc.),
// preserving entity designators and small connector words. Values with any
// lowercase letters pass through untouched - only full-caps values are fixed.
function normalizeNameCase(v) {
  const str = String(v || '');
  if (!str || str !== str.toUpperCase() || !/[A-Z]/.test(str)) return str;
  const KEEP = new Set(['LLC','LLP','PLLC','LP','PC','PA','INC','II','III','IV','CPA','DBA']);
  const SMALL = new Set(['AND','OF','THE','FOR']);
  return str.split(/\s+/).map((w, i) => {
    const bare = w.replace(/[^A-Z0-9&]/g, '');
    if (KEEP.has(bare)) return w;
    if (i > 0 && SMALL.has(bare)) return w.toLowerCase();
    return w.charAt(0) + w.slice(1).toLowerCase();
  }).join(' ');
}

// Derives the Personal / Business ownership flag from the account holder name.
// An entity marker in the name means Business; humans mean Personal. Written to
// Airtable silently - the question left the form because the name answers it.
function deriveOwnerType(name) {
  const n = ' ' + String(name || '').toUpperCase().replace(/[.,]/g, ' ') + ' ';
  const ENTITY = [' LLC ',' LLP ',' PLLC ',' LP ',' INC ',' CORP ',' CORPORATION ',' COMPANY ',' CO ',' TRUST ',' PC ',' PA ',' DBA ',' LTD '];
  return ENTITY.some(t => n.includes(t)) ? 'Business' : 'Personal';
}

// Portal display name: hide the category prefix letter (and archived marker).
// Drive keeps the full prefixed filename; this is display only.
function displayDocName(name) {
  let n = String(name || '');
  n = n.replace(/^ZZZ_ARCHIVED_/, '');
  n = n.replace(/^[A-FLMX] - /, '');
  return n;
}

// ============ MAIN COMPONENT ============

export default function DocumentsCard({ deal, borrowerContactId, collapsed, onToggle, title = 'DOCUMENTS' }) {
  const driveFolderId = deal?.fields?.['Drive folder ID'] || '';
  const driveFolderUrl = deal?.fields?.['Drive folder URL'] || '';
  const dealId = deal?.id;

  const [documents, setDocuments] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [replaceModal, setReplaceModal] = useState(null);
  const [uploadQueue, setUploadQueue] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [extractingDocIds, setExtractingDocIds] = useState(new Set());
  // Tracks which category groups are collapsed. By default ALL categories are
  // expanded - users collapse the ones they want to hide. Stored as a Set of
  // category strings (e.g. "B - Borrower / Sponsor") so it survives refresh
  // refetches but not full page reloads (acceptable for v1).
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`/.netlify/functions/list-documents?dealId=${dealId}&includeArchived=${showArchived}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load documents');
      setDocuments(data.documents || []);
      setByCategory(data.byCategory || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dealId, showArchived]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleCategory = (cat) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilesSelected(e.dataTransfer.files);
  };

  const handleFilesSelected = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const bad = [];
    const good = [];
    for (const f of files) {
      const ext = fileExtension(f.name);
      if (!ALLOWED_EXTENSIONS.includes(ext)) { bad.push(`"${f.name}": file type ".${ext}" is not allowed.`); continue; }
      if (f.size > MAX_FILE_SIZE) { bad.push(`"${f.name}": too large (${(f.size / 1024 / 1024).toFixed(1)}MB, max 50MB).`); continue; }
      good.push(f);
    }
    if (bad.length) alert('These files were skipped:\n\n' + bad.join('\n'));
    if (!good.length) return;
    setUploadQueue(good);
    setUploadModalOpen(true);
  };

  const handlePickFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ALLOWED_EXTENSIONS.map(e => '.' + e).join(',');
    input.onchange = (e) => handleFilesSelected(e.target.files);
    input.click();
  };

  const handleStatusChange = async (doc, newStatus) => {
    try {
      const res = await authFetch('/.netlify/functions/update-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: doc.id, action: 'status', status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update status');
      await refresh();
    } catch (e) {
      alert('Status update failed: ' + e.message);
    }
  };

  const handleArchive = async (doc, archive) => {
    if (archive && !window.confirm(`Archive "${doc.documentName}"?\n\nThe file stays in Drive but is hidden from the default view.`)) return;
    try {
      const res = await authFetch('/.netlify/functions/update-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: doc.id, action: archive ? 'archive' : 'unarchive' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to (un)archive');
      await refresh();
    } catch (e) {
      alert('(Un)archive failed: ' + e.message);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${displayDocName(doc.documentName)}"?\n\nThis removes the document from the portal permanently. The Drive file is kept but renamed with an archive prefix, so nothing is destroyed.`)) return;
    try {
      const res = await authFetch('/.netlify/functions/update-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: doc.id, action: 'delete' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete');
      await refresh();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  const handleMoveToXFiles = async (doc) => {
    if (!window.confirm(`Move "${doc.documentName}" to X-Files?\n\nThe document keeps its real doc type but is filed under X - X-Files (Admin only) and will be excluded from lender packages. The Drive file is renamed with an X prefix; the link stays the same.`)) return;
    try {
      const res = await authFetch('/.netlify/functions/update-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: doc.id, action: 'moveToXFiles' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to move to X-Files');
      await refresh();
    } catch (e) {
      alert('Move to X-Files failed: ' + e.message);
    }
  };

  const handleRunAI = async (doc) => {
    if (extractingDocIds.has(doc.id)) return;
    setExtractingDocIds((prev) => {
      const next = new Set(prev);
      next.add(doc.id);
      return next;
    });
    try {
      const res = await authFetch('/.netlify/functions/extract-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: doc.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && !data.queued) throw new Error(data.error || 'Could not start AI extraction');
      // Extraction now runs in the background. Show the Extracting pill, then
      // poll the document's status until it settles, then refresh.
      await refresh();
      const done = await pollExtractionStatus(doc.id);
      if (done && done.aiReviewStatus === 'Failed') {
        alert('AI extraction failed: ' + (done.summary || 'see the document for details'));
      } else if (!done) {
        alert('AI extraction is taking longer than expected. It may still finish - use Refresh in a moment.');
      }
      await refresh();
    } catch (e) {
      alert('AI extraction failed: ' + e.message);
      try { await refresh(); } catch {}
    } finally {
      setExtractingDocIds((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  if (!driveFolderId) {
    return (
      <div style={s.card}>
        <div style={s.cardHead}>
          <i className="ti ti-file" style={{ color:'#fff', fontSize:'14px' }}/>
          <div style={{ ...s.cardTitle, flex:1 }}>{title}</div>
          <button style={s.collapseBtn} onClick={() => onToggle && onToggle('docs')}>
            <i className={`ti ${collapsed ? 'ti-chevron-right' : 'ti-chevron-down'}`}/>
          </button>
        </div>
        {!collapsed && (
          <div style={s.cardBody}>
            <div style={{ ...s.emptyText, padding:'8px 0' }}>
              <i className="ti ti-alert-circle" style={{ marginRight:'6px', color:'#7A4A08' }}/>
              This deal doesn&apos;t have a Drive folder yet. The folder is created automatically on deal intake.
              If this deal was created before that feature was deployed, contact admin to create the folder manually.
            </div>
          </div>
        )}
      </div>
    );
  }

  const sortedCategoryKeys = Object.keys(byCategory).sort();

  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <i className="ti ti-file" style={{ color:'#fff', fontSize:'14px' }}/>
        <div style={{ ...s.cardTitle, flex:1 }}>{title}</div>
        <button style={s.collapseBtn} onClick={() => onToggle && onToggle('docs')}>
          <i className={`ti ${collapsed ? 'ti-chevron-right' : 'ti-chevron-down'}`}/>
        </button>
      </div>

      {!collapsed && (
        <div
          style={dragOver ? { ...s.cardBody, outline:'2px dashed #B8D4F0', outlineOffset:'-6px', background:'#F7FAFD' } : s.cardBody}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {(()=>{
            const all = documents||[];
            const nApproved = all.filter(x=>x.status==='Approved').length;
            const nReview = all.filter(x=>x.status==='Under review').length;
            const nAttn = all.filter(x=>x.status==='Requested'||x.status==='Needs replacement').length;
            const dTile = (k,v,amber) => (<div style={{minWidth:'80px'}}><div style={{fontFamily:"'Inter',sans-serif",fontSize:'9px',fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',color:'#7189B0'}}>{k}</div><div style={{fontSize:'14px',fontWeight:600,color:amber?'#8a5a12':'#154D97',marginTop:'2px'}}>{v}</div></div>);
            const dDiv = (key) => (<div key={key} style={{width:'1px',alignSelf:'stretch',background:'#D9E5F4'}}/>);
            return (<div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',background:'#F0F5FB',border:'1px solid #D9E5F4',borderRadius:'11px',padding:'10px 14px',marginBottom:'12px'}}>
              {dTile('On file',all.length,false)}{dDiv('d1')}
              {dTile('Approved',nApproved,false)}{dDiv('d2')}
              {dTile('Under review',nReview,false)}{dDiv('d3')}
              {dTile('Needs attention',nAttn,nAttn>0)}
            </div>);
          })()}
          <div style={s.toolbar}>
            <a href={driveFolderUrl} target="_blank" rel="noopener noreferrer"
               style={{ fontSize:'12px', color:'#154D97', textDecoration:'none', display:'flex', alignItems:'center', gap:'4px', marginRight:'auto' }}>
              <i className="ti ti-external-link" style={{ fontSize:'13px' }}/>
              Open Drive folder
            </a>
            <label style={s.archiveToggle}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
                style={{ accentColor:'#154D97', width:'14px', height:'14px' }}/>
              Show archived
            </label>
            <button style={s.uploadBtn} onClick={handlePickFile}>
              <i className="ti ti-upload" style={{ fontSize:'14px' }}/>
              Upload document
            </button>
          </div>

          {error && <div style={s.errorMsg}>{error}</div>}
          {loading && <div style={s.emptyText}>Loading documents…</div>}
          {!loading && documents.length === 0 && (
            <div style={{ ...s.emptyText, padding:'12px 0' }}>No documents uploaded yet. Drag a file anywhere here or use Upload.</div>
          )}

          {!loading && documents.length > 0 && (
            <div style={s.colHeadRow}>
              <div style={s.colHead}>Document</div>
              <div style={s.colHead}>Status</div>
              <div style={s.colHeadR}>Actions</div>
            </div>
          )}

          {sortedCategoryKeys.map(cat => {
            const isCollapsed = collapsedCategories.has(cat);
            const docs = byCategory[cat] || [];
            return (
              <div key={cat} style={s.catGroup}>
                <div style={s.catHeader} onClick={() => toggleCategory(cat)}>
                  <div style={s.catHeaderLeft}>
                    <i className={`ti ${isCollapsed ? 'ti-chevron-right' : 'ti-chevron-down'}`} style={{ fontSize:'12px' }}/>
                    <span>{cat}</span>
                  </div>
                  <span style={s.catHeaderRule}/>
                  <span style={s.catHeaderCount}>{docs.length}</span>
                </div>
                {!isCollapsed && docs.map(doc => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    isExtracting={extractingDocIds.has(doc.id)}
                    isExtractable={EXTRACTABLE_DOC_TYPES.has(doc.docType)}
                    onStatusChange={handleStatusChange}
                    onEdit={() => setEditModal({ document: doc })}
                    onReplace={() => setReplaceModal({ document: doc })}
                    onArchive={() => handleArchive(doc, !doc.archived)}
                    onDelete={() => handleDelete(doc)}
                    onMoveToXFiles={() => handleMoveToXFiles(doc)}
                    onRunAI={() => handleRunAI(doc)}
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {uploadModalOpen && (
        <UploadModal
          deal={deal}
          borrowerContactId={borrowerContactId}
          driveFolderId={driveFolderId}
          queue={uploadQueue}
          replacesDocumentId={null}
          onUploadedOne={refresh}
          onClose={async () => { setUploadModalOpen(false); setUploadQueue(null); await refresh(); }}
          onSuccess={async () => { setUploadModalOpen(false); setUploadQueue(null); await refresh(); }}
        />
      )}

      {replaceModal && (
        <UploadModal
          deal={deal}
          borrowerContactId={borrowerContactId}
          driveFolderId={driveFolderId}
          replacesDocumentId={replaceModal.document.id}
          existingDocType={replaceModal.document.docType}
          onClose={() => setReplaceModal(null)}
          onSuccess={async () => { setReplaceModal(null); await refresh(); }}
        />
      )}

      {editModal && (
        <EditMetadataModal
          document={editModal.document}
          onClose={() => setEditModal(null)}
          onSuccess={async () => { setEditModal(null); await refresh(); }}
        />
      )}
    </div>
  );
}

// Renders the "Discrepancies flagged" text. The backend writes it as labeled
// sections (NEEDS REVIEW / FOR YOUR AWARENESS / NOTES) separated by blank lines.
// We color each heading: amber for NEEDS REVIEW, blue for awareness, grey notes.
function renderFlagText(text) {
  const lines = String(text).split('\n');
  return lines.map((line, i) => {
    const t = line.trim();
    if (t === 'NEEDS REVIEW')       return <span key={i} style={s.flagHeadReview}>{t}</span>;
    if (t === 'FOR YOUR AWARENESS') return <span key={i} style={s.flagHeadAware}>{t}</span>;
    if (t === 'NOTES')              return <span key={i} style={s.flagHeadNotes}>{t}</span>;
    return <span key={i}>{line}{'\n'}</span>;
  });
}

// Counts items per section so the collapsed header can summarize at a glance.
// Handles both formats:
//   new  -> "NEEDS REVIEW" headers, items on "- " lines
//   old  -> "🚨 Needs Review" inline, items on "• " markers
// Returns { needsReview, forAwareness, notes }.
function countFlags(text) {
  const raw = String(text || '');
  const counts = { needsReview: 0, forAwareness: 0, notes: 0 };
  if (!raw.trim()) return counts;

  // Detect old single-line emoji format and split it into chunks.
  if (raw.includes('🚨') || raw.includes('💡') || raw.includes('ℹ️') || raw.includes('•')) {
    const bucketOf = (chunk) => {
      if (chunk.includes('Needs Review') || chunk.includes('NEEDS REVIEW')) return 'needsReview';
      if (chunk.includes('Awareness') || chunk.includes('AWARENESS')) return 'forAwareness';
      return 'notes';
    };
    // Split on the section emojis, keep order.
    const parts = raw.split(/(?=🚨|💡|ℹ️)/);
    for (const part of parts) {
      if (!part.trim()) continue;
      const bucket = bucketOf(part);
      const items = (part.match(/•/g) || []).length;
      counts[bucket] += items;
    }
    return counts;
  }

  // New plain format: walk lines, track current section, count "- " items.
  let section = null;
  for (const line of raw.split('\n')) {
    const t = line.trim();
    if (t === 'NEEDS REVIEW')       { section = 'needsReview';  continue; }
    if (t === 'FOR YOUR AWARENESS') { section = 'forAwareness'; continue; }
    if (t === 'NOTES')              { section = 'notes';        continue; }
    if (section && t.startsWith('-')) counts[section] += 1;
  }
  return counts;
}

// ============ DOCUMENT ROW ============

function DocumentRow({ doc, isExtracting, isExtractable, onStatusChange, onEdit, onReplace, onArchive, onDelete, onMoveToXFiles, onRunAI }) {
  const rowStyle = doc.archived ? s.docRowArchived : s.docRow;
  const inXFiles = doc.category === 'X - X-Files (Admin only)';

  const aiStatus = isExtracting ? 'Extracting' : (doc.aiReviewStatus || 'Not started');
  const aiActive = isExtractable && aiStatus !== 'Not applicable';

  const aiCompleted = (aiStatus === 'Reviewed - clean' || aiStatus === 'Reviewed - flagged');
  const [flagsOpen, setFlagsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuDropUp, setMenuDropUp] = useState(false);
  const flagCounts = countFlags(doc.discrepanciesFlagged);
  const hasNeedsReview = flagCounts.needsReview > 0;

  const aiKind = isExtracting ? 'busy'
    : (aiStatus === 'Reviewed - flagged' || hasNeedsReview) ? 'flag'
    : aiStatus === 'Reviewed - clean' ? 'clean'
    : aiStatus === 'Failed' ? 'fail'
    : 'pending';
  const aiTitle =
    aiStatus === 'Failed' ? (doc.aiExtractedSummary || 'AI extraction failed') :
    aiStatus === 'Reviewed - clean' ? 'AI review complete, no red flags. Click for detail.' :
    aiStatus === 'Reviewed - flagged' ? [
      flagCounts.needsReview  ? `${flagCounts.needsReview} needs review`  : null,
      flagCounts.forAwareness ? `${flagCounts.forAwareness} awareness`    : null,
      flagCounts.notes        ? `${flagCounts.notes} notes`               : null,
    ].filter(Boolean).join(' · ') + '. Click for detail.' :
    isExtracting ? 'AI is reading this document now' :
    'AI has not reviewed this document yet';

  const closeMenu = () => setMenuOpen(false);

  return (
    <div style={rowStyle}>
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:'9px', minWidth:0 }}>
          {aiActive && (
            <span style={s.aiDot(aiKind)} title={aiTitle}
              onClick={() => { if (aiCompleted && (doc.aiExtractedSummary || doc.discrepanciesFlagged)) setFlagsOpen(o => !o); }}>
              AI
            </span>
          )}
          <div style={s.docName}>
            {doc.driveLink ? (
              <a href={doc.driveLink} target="_blank" rel="noopener noreferrer"
                 style={{ color:'#2E4763', textDecoration:'none' }}>
                {displayDocName(doc.documentName)}
              </a>
            ) : displayDocName(doc.documentName)}
          </div>
        </div>
        {flagsOpen && aiCompleted && (doc.aiExtractedSummary || doc.discrepanciesFlagged) && (
          <div style={s.aiPanel}>
            {doc.aiExtractedSummary && (
              <div style={s.aiSummary}>{doc.aiExtractedSummary}</div>
            )}
            {doc.discrepanciesFlagged && (
              <div style={doc.aiExtractedSummary ? s.flagBlock : s.flagBlockNoTop}>{renderFlagText(doc.discrepanciesFlagged)}</div>
            )}
          </div>
        )}
      </div>

      <div style={s.statusCol}><span style={s.statusPill(doc.status)}>{doc.status}</span></div>

      <div style={s.docActions}>
        {doc.driveLink ? (
          <a href={doc.driveLink} target="_blank" rel="noopener noreferrer" style={s.viewBtn} title="View document">
            <i className="ti ti-eye" style={{ fontSize:'15px' }}/>
          </a>
        ) : (
          <span style={s.viewBtnDim} title="No file linked yet">
            <i className="ti ti-eye" style={{ fontSize:'15px' }}/>
          </span>
        )}
        <div style={s.menuWrap}>
          <button style={s.menuBtn} title="More actions" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenuDropUp(window.innerHeight - r.bottom < 360); setMenuOpen(o => !o); }}>
            <i className="ti ti-dots" style={{ fontSize:'16px' }}/>
          </button>
          {menuOpen && (
            <>
              <div style={s.menuBackdrop} onClick={closeMenu}/>
              <div style={menuDropUp ? { ...s.menu, top:'auto', bottom:'28px' } : s.menu}>
                <div style={s.menuLabel}>Set status</div>
                {STATUS_OPTIONS.map(o => (
                  <button key={o} style={s.menuStatusItem(o === doc.status)}
                    onClick={() => { onStatusChange(doc, o); closeMenu(); }}>
                    <i className="ti ti-check" style={{ fontSize:'12px', opacity: o === doc.status ? 1 : 0 }}/>
                    {o}
                  </button>
                ))}
                <div style={s.menuDivider}/>
                {isExtractable && (
                  <button style={s.menuItem} disabled={isExtracting}
                    onClick={() => { onRunAI(); closeMenu(); }}>
                    <i className={`ti ${isExtracting ? 'ti-loader-2' : 'ti-sparkles'}`} style={{ fontSize:'14px' }}/>
                    {isExtracting ? 'AI running…' : 'Run AI review'}
                  </button>
                )}
                <button style={s.menuItem} onClick={() => { onEdit(); closeMenu(); }}>
                  <i className="ti ti-pencil" style={{ fontSize:'14px' }}/> Edit metadata
                </button>
                <button style={s.menuItem} onClick={() => { onReplace(); closeMenu(); }}>
                  <i className="ti ti-refresh" style={{ fontSize:'14px' }}/> Replace file
                </button>
                {!inXFiles && (
                  <button style={s.menuItem} onClick={() => { onMoveToXFiles(); closeMenu(); }}>
                    <i className="ti ti-eye-off" style={{ fontSize:'14px' }}/> Move to X-Files
                  </button>
                )}
                <button style={s.menuItemDanger} onClick={() => { onArchive(); closeMenu(); }}>
                  <i className={`ti ${doc.archived ? 'ti-archive-off' : 'ti-archive'}`} style={{ fontSize:'14px' }}/>
                  {doc.archived ? 'Unarchive' : 'Archive'}
                </button>
                <button style={s.menuItemDanger} onClick={() => { onDelete(); closeMenu(); }}>
                  <i className="ti ti-trash" style={{ fontSize:'14px' }}/> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ UPLOAD MODAL ============

function UploadModal({ deal, borrowerContactId, driveFolderId, queue, replacesDocumentId, existingDocType, onClose, onSuccess, onUploadedOne }) {
  const queueMode = Array.isArray(queue) && queue.length > 0;
  const [files, setFiles] = useState(queueMode ? queue : []);
  const [pos, setPos] = useState(0);
  const [doneNames, setDoneNames] = useState([]);
  const [file, setFile] = useState(queueMode ? queue[0] : null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [category, setCategory] = useState(existingDocType ? (DOC_TYPES[existingDocType]?.prefix || '') : '');
  const [docTypeKey, setDocTypeKey] = useState(existingDocType || '');
  const [fields, setFields] = useState({});
  const [fileIntoX, setFileIntoX] = useState(false);
  const [conditionRequest, setConditionRequest] = useState(false);
  const [conditionNote, setConditionNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const uploadingRef = useRef(false);
  const confirmedRef = useRef(false);
  const userTouchedRef = useRef(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestedType, setSuggestedType] = useState('');
  const [suggestNote, setSuggestNote] = useState('');

  const docTypeDef = docTypeKey ? DOC_TYPES[docTypeKey] : null;

  useEffect(() => {
    if (!file) { setPreviewUrl(''); return; }
    const u = URL.createObjectURL(file);
    setPreviewUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  // AI type suggestion: read the queued file, ask classify-document for its
  // best guess, and pre-select it ONLY if the user hasn't touched the
  // category or type yet. Advisory always; any failure just leaves the
  // dropdowns empty exactly as before.
  useEffect(() => {
    if (!file || existingDocType) { setSuggesting(false); setSuggestedType(''); setSuggestNote(''); return; }
    const fext = fileExtension(file.name);
    const mime = fext === 'pdf' ? 'application/pdf'
      : ['jpg','jpeg'].includes(fext) ? 'image/jpeg'
      : fext === 'png' ? 'image/png'
      : fext === 'webp' ? 'image/webp'
      : fext === 'gif' ? 'image/gif'
      : null;
    if (!mime) {
      setSuggesting(false); setSuggestedType('');
      setSuggestNote(`No AI suggestion for .${fext} files. Pick the type below.`);
      return;
    }
    if (file.size > 4.5 * 1024 * 1024) {
      setSuggesting(false); setSuggestedType('');
      setSuggestNote(`This file is ${(file.size / 1024 / 1024).toFixed(1)}MB, over the 4.5MB AI suggestion limit. Pick the type below.`);
      return;
    }

    let cancelled = false;
    setSuggesting(true);
    setSuggestedType('');
    setSuggestNote('');
    const reader = new FileReader();
    reader.onerror = () => { if (!cancelled) setSuggesting(false); };
    reader.onload = async () => {
      try {
        const b64 = String(reader.result).split(',')[1];
        const res = await authFetch('/.netlify/functions/classify-document', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mimeType: mime,
            data: b64,
            fileName: file.name,
            types: Object.entries(DOC_TYPES).map(([k, d]) => ({
              key: k,
              fields: d.fields.map(f => ({ key: f.key, label: f.label, type: f.type, options: f.options || null })),
            })),
          }),
        });
        const d = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && d.docType && DOC_TYPES[d.docType] && !userTouchedRef.current) {
          const fx = { ...(d.fields || {}) };
          for (const k of ['ownerName', 'entityName', 'institution', 'donorName', 'lenderName']) {
            if (fx[k]) fx[k] = normalizeNameCase(fx[k]);
          }
          setCategory(DOC_TYPES[d.docType].prefix);
          setDocTypeKey(d.docType);
          setFields(fx);
          setSuggestedType(d.docType);
        } else if (!userTouchedRef.current) {
          setSuggestNote(res.ok
            ? 'The AI could not confidently identify this document. Pick the type below.'
            : 'AI suggestion unavailable for this file (' + (d.error || res.status) + '). Pick the type below.');
        }
      } catch (e) {
        console.warn('classify-document suggestion failed (non-blocking):', e);
      } finally {
        if (!cancelled) setSuggesting(false);
      }
    };
    reader.readAsDataURL(file);
    return () => { cancelled = true; };
  }, [file, existingDocType]); // eslint-disable-line

  const resetPerFile = () => {
    setCategory(''); setDocTypeKey(''); setFields({}); setFileIntoX(false);
    setConditionRequest(false); setConditionNote(''); setError(''); setProgress(0);
    setUploading(false);
    setSuggestedType('');
    setSuggestNote('');
    uploadingRef.current = false;
    confirmedRef.current = false;
    userTouchedRef.current = false;
  };

  const advanceQueue = () => {
    const next = pos + 1;
    if (next >= files.length) { onSuccess(); return; }
    setPos(next);
    setFile(files[next]);
    resetPerFile();
  };

  const skipCurrent = () => {
    if (uploading) return;
    // Last file: skipping it means finishing the queue without uploading it.
    if (pos >= files.length - 1) { onSuccess(); return; }
    const c = [...files];
    const [sk] = c.splice(pos, 1);
    c.push(sk);
    setFiles(c);
    setFile(c[pos]);
    resetPerFile();
  };

  const docTypesInCategory = category
    ? (category === 'X'
        ? Object.entries(DOC_TYPES).map(([k]) => ({ key: k, label: k }))
        : Object.entries(DOC_TYPES).filter(([, d]) => d.prefix === category).map(([k]) => ({ key: k, label: k })))
    : [];

  const ext = file ? fileExtension(file.name) : '';
  const previewFilename = docTypeKey && file
    ? buildPreviewFilename(docTypeKey, fields, ext, (category === 'X' || fileIntoX) ? 'X' : null)
    : '';

  const pickFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_EXTENSIONS.map(e => '.' + e).join(',');
    input.onchange = (e) => {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      const fext = fileExtension(f.name);
      if (!ALLOWED_EXTENSIONS.includes(fext)) {
        setError(`File type ".${fext}" is not allowed.`);
        return;
      }
      if (f.size > MAX_FILE_SIZE) {
        setError(`File too large (${(f.size / 1024 / 1024).toFixed(1)}MB). Max 50MB.`);
        return;
      }
      setError('');
      setFile(f);
    };
    input.click();
  };

  const validate = () => {
    if (!file) return 'Please select a file.';
    if (!category) return 'Please pick a category.';
    if (!docTypeKey) return 'Please pick a document type.';
    if (!docTypeDef) return 'Unknown document type.';
    for (const fld of docTypeDef.fields) {
      if (fld.required) {
        const v = fields[fld.key];
        if (v === undefined || v === null || String(v).trim() === '') {
          return `Please fill in "${fld.label}".`;
        }
      }
    }
    return null;
  };

  const handleUpload = async () => {
    if (uploadingRef.current) {
      console.warn('Upload already in progress, ignoring duplicate click.');
      return;
    }

    const errMsg = validate();
    if (errMsg) { setError(errMsg); return; }

    uploadingRef.current = true;
    confirmedRef.current = false;
    setUploading(true);
    setError('');
    setProgress(0);

    const categoryOverride = (category === 'X' || fileIntoX) ? 'X' : null;
    const STATEMENT_TYPES = ['Bank Statement','Brokerage Statement','Mortgage Statement','HELOC Statement','Loan Statement'];
    const sendFields = STATEMENT_TYPES.includes(docTypeKey) && fields.ownerName
      ? { ...fields, ownerType: deriveOwnerType(fields.ownerName) }
      : fields;

    try {
      const sessionRes = await authFetch('/.netlify/functions/get-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driveFolderId,
          docTypeKey,
          fields: sendFields,
          categoryOverride,
          fileExtension: ext,
          fileSize: file.size,
          originalFilename: file.name,
          dealName: deal?.fields?.['Deal name'] || '',
        }),
      });
      const sessionData = await sessionRes.json();
      if (!sessionRes.ok) throw new Error(sessionData.error || 'Failed to start upload');
      const { sessionUri, mimeType } = sessionData;

      const driveFile = await uploadToDriveAndGetFileMetadata({
        sessionUri,
        file,
        mimeType,
        onProgress: setProgress,
      });

      if (!driveFile || !driveFile.id) {
        throw new Error('Upload completed but Drive did not return a file ID.');
      }

      setProgress(100);

      if (confirmedRef.current) {
        console.warn('confirm-upload already called for this file. Skipping.');
        onSuccess();
        return;
      }
      confirmedRef.current = true;

      const confirmRes = await authFetch('/.netlify/functions/confirm-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driveFileId: driveFile.id,
          dealId: deal.id,
          contactId: borrowerContactId || null,
          docTypeKey,
          fields: sendFields,
          categoryOverride,
          conditionRequest,
          conditionNote: conditionRequest ? conditionNote : '',
          replacesDocumentId: replacesDocumentId || null,
        }),
      });
      const confirmData = await confirmRes.json();
      if (!confirmRes.ok) throw new Error(confirmData.error || 'Failed to record upload');

      if (queueMode) {
        setDoneNames(n => [...n, file.name]);
        if (onUploadedOne) { try { await onUploadedOne(); } catch {} }
        if (pos + 1 < files.length) { advanceQueue(); }
        else { onSuccess(); }
      } else {
        onSuccess();
      }
    } catch (e) {
      console.error(e);
      setError(e.message);
      setUploading(false);
      uploadingRef.current = false;
    }
  };

  const ptExt = ext;
  const previewKind = !file ? '' : (ptExt === 'pdf' ? 'pdf' : (['jpg','jpeg','png','gif','webp'].includes(ptExt) ? 'img' : 'none'));

  return (
    <div style={s.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !uploading) onClose(); }}>
      <div style={file ? { ...s.modal, maxWidth:'1240px' } : s.modal}>
        <div style={s.modalHead}>
          <div style={s.modalTitle}>
            {replacesDocumentId ? 'REPLACE DOCUMENT' : (queueMode ? 'UPLOAD DOCUMENTS' : 'UPLOAD DOCUMENT')}
          </div>
          {queueMode && (
            <span style={{ marginLeft:'auto', marginRight:'14px', fontSize:'12px', fontWeight:600, color:'#154D97', background:'#fff', border:'1px solid #C9D9EE', borderRadius:'999px', padding:'3px 12px' }}>
              File {Math.min(pos + 1, files.length)} of {files.length}
            </span>
          )}
          <button style={s.modalClose} onClick={onClose} disabled={uploading}>×</button>
        </div>
        <div style={file ? { ...s.modalBody, display:'flex', gap:'16px', alignItems:'stretch' } : s.modalBody}>
          {file && (
            <div style={{ flex:'1.7 1 0', minWidth:0, background:'#3A4A61', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', minHeight:'560px', overflow:'hidden' }}>
              {previewKind === 'pdf' && (
                <iframe title="Document preview" src={previewUrl + '#toolbar=1&navpanes=0&view=FitH'} style={{ width:'100%', height:'76vh', minHeight:'560px', border:'none', background:'#fff' }}/>
              )}
              {previewKind === 'img' && (
                <img alt="Document preview" src={previewUrl} style={{ maxWidth:'100%', maxHeight:'76vh', objectFit:'contain' }}/>
              )}
              {previewKind === 'none' && (
                <div style={{ color:'#C9D5E6', fontSize:'13px', textAlign:'center', padding:'24px' }}>
                  <i className="ti ti-file" style={{ fontSize:'34px', display:'block', marginBottom:'10px' }}/>
                  No preview for .{ptExt} files
                  <div style={{ marginTop:'8px', fontWeight:600, color:'#fff', wordBreak:'break-all' }}>{file.name}</div>
                </div>
              )}
            </div>
          )}
          <div style={file ? { flex:'1 1 0', minWidth:0, maxHeight:'76vh', overflowY:'auto', paddingRight:'2px' } : undefined}>
          {file ? (
            <div style={s.filePreview}>
              <i className="ti ti-file" style={{ fontSize:'14px' }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, wordBreak:'break-all' }}>Your file: {file.name}</div>
                <div style={{ fontSize:'11px', color:'#8a9ab5' }}>{(file.size / 1024).toFixed(1)} KB</div>
              </div>
              {!uploading && !queueMode && (
                <button style={{ ...s.iconBtn, marginLeft:'auto' }} onClick={pickFile}>Change</button>
              )}
            </div>
          ) : (
            <button style={{ ...s.uploadBtn, width:'100%', justifyContent:'center', marginBottom:'12px' }} onClick={pickFile}>
              <i className="ti ti-upload"/> Pick a file
            </button>
          )}

          {suggesting && (
            <div style={{ fontSize:'12px', color:'#4A7FBF', background:'#F0F5FB', border:'1px solid #D9E5F4', borderRadius:'8px', padding:'7px 11px', marginBottom:'10px' }}>
              Reading the document to suggest a type...
            </div>
          )}
          {!suggesting && !suggestedType && suggestNote && (
            <div style={{ fontSize:'12px', color:'#8a5a12', background:'#FAEEDA', border:'1px solid #EED9B4', borderRadius:'8px', padding:'7px 11px', marginBottom:'10px' }}>
              {suggestNote}
            </div>
          )}
          {!suggesting && suggestedType && (
            <div style={{ fontSize:'12px', color:'#3B6D11', background:'#EAF3DE', border:'1px solid #CBE3B4', borderRadius:'8px', padding:'7px 11px', marginBottom:'10px' }}>
              {'AI suggests: ' + suggestedType + '. Confirm below or change anything that is wrong.'}
            </div>
          )}
          <div style={s.field}>
            <div style={s.fieldLabel}>Category<span style={s.fieldReq}>*</span></div>
            <select style={s.select} value={category}
              disabled={!!existingDocType || uploading}
              onChange={(e) => { userTouchedRef.current = true; setSuggestedType(''); setCategory(e.target.value); setDocTypeKey(''); setFields({}); setFileIntoX(false); }}>
              <option value="">Select…</option>
              {CATEGORIES.map(c => <option key={c.prefix} value={c.prefix}>{c.label}</option>)}
            </select>
          </div>

          {category && (
            <div style={{ ...s.field, marginTop:'12px' }}>
              <div style={s.fieldLabel}>Document type<span style={s.fieldReq}>*</span></div>
              <select style={s.select} value={docTypeKey}
                disabled={!!existingDocType || uploading}
                onChange={(e) => { userTouchedRef.current = true; setSuggestedType(''); setDocTypeKey(e.target.value); setFields({}); }}>
                <option value="">Select…</option>
                {docTypesInCategory.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
              {category === 'X' && (
                <div style={{ ...s.subHint, marginTop:'4px' }}>
                  Filing into X-Files: the document keeps its real doc type but is admin-only and excluded from lender packages.
                </div>
              )}
            </div>
          )}

          {docTypeDef && docTypeDef.fields.length > 0 && (
            <div style={{ marginTop:'14px' }}>
              <div style={s.subLabel}>Document metadata</div>
              <div style={s.fieldGrid}>
                {docTypeDef.fields.map(fld => (
                  <DynamicField key={fld.key} field={fld} value={fields[fld.key] || ''}
                    onChange={(v) => setFields(p => ({ ...p, [fld.key]: v }))}
                    disabled={uploading}/>
                ))}
              </div>
            </div>
          )}

          {previewFilename && (
            <div style={{ marginTop:'10px' }}>
              <div style={s.subLabel}>Filename preview</div>
              <div style={s.filenamePreview}>{previewFilename}</div>
            </div>
          )}

          {!replacesDocumentId && (
            <div style={s.condBox}>
              <label style={s.condToggle}>
                <input type="checkbox" checked={conditionRequest}
                  onChange={(e) => setConditionRequest(e.target.checked)}
                  disabled={uploading}
                  style={{ accentColor:'#7A4A08', width:'15px', height:'15px' }}/>
                This document is a lender condition (requested after submission)
              </label>
              {conditionRequest && (
                <div style={{ ...s.field, marginTop:'10px' }}>
                  <div style={s.fieldLabel}>Condition note (optional)</div>
                  <input style={s.input} value={conditionNote}
                    onChange={(e) => setConditionNote(e.target.value)}
                    disabled={uploading}
                    placeholder="e.g. Lender X requested updated bank statement, 5/20"/>
                </div>
              )}
            </div>
          )}

          {!replacesDocumentId && category && category !== 'X' && (
            <div style={{ marginTop:'12px' }}>
              <label style={s.archiveToggle}>
                <input type="checkbox" checked={fileIntoX}
                  onChange={(e) => setFileIntoX(e.target.checked)}
                  disabled={uploading}
                  style={{ accentColor:'#154D97', width:'14px', height:'14px' }}/>
                File into X-Files (admin-only, excluded from lender packages)
              </label>
            </div>
          )}

          {uploading && (
            <div style={{ marginTop:'14px' }}>
              <div style={{ fontSize:'12px', color:'#4A7FBF', marginBottom:'4px' }}>
                Uploading… {progress}%
              </div>
              <div style={s.progressBar}>
                <div style={s.progressFill(progress)}/>
              </div>
            </div>
          )}

          {error && <div style={s.errorMsg}>{error}</div>}

          <div style={s.modalActions}>
            <button style={s.cancelBtn} onClick={onClose} disabled={uploading}>{queueMode ? 'Stop here' : 'Cancel'}</button>
            {queueMode && (
              <button style={s.cancelBtn} onClick={skipCurrent} disabled={uploading}>
                {pos < files.length - 1 ? 'Skip for now' : 'Skip, finish'}
              </button>
            )}
            <button style={s.primaryBtn} onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading…' : (replacesDocumentId ? 'Replace' : (queueMode ? (pos < files.length - 1 ? 'Upload, next file' : 'Upload, finish') : 'Upload'))}
            </button>
          </div>
          </div>
        </div>
        {queueMode && (
          <div style={{ borderTop:'1px solid #D9E5F4', background:'#F0F5FB', padding:'9px 16px', display:'flex', gap:'7px', overflowX:'auto' }}>
            {doneNames.map((n, z) => (
              <span key={'d' + z} style={{ fontSize:'11px', borderRadius:'999px', padding:'4px 11px', whiteSpace:'nowrap', border:'1px solid #CBE3B4', background:'#EAF3DE', color:'#3B6D11', fontWeight:600 }}>{'\u2713 ' + n}</span>
            ))}
            {files.slice(pos).map((f, z) => (
              <span key={'p' + z} style={z === 0
                ? { fontSize:'11px', borderRadius:'999px', padding:'4px 11px', whiteSpace:'nowrap', border:'1px solid #154D97', background:'#154D97', color:'#fff', fontWeight:600 }
                : { fontSize:'11px', borderRadius:'999px', padding:'4px 11px', whiteSpace:'nowrap', border:'1px solid #C9D9EE', background:'#fff', color:'#4A7FBF' }}>{f.name}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ EDIT METADATA MODAL ============

function EditMetadataModal({ document: doc, onClose, onSuccess }) {
  const initFieldsFor = (typeKey) => {
    const def = DOC_TYPES[typeKey] || null;
    const initial = {};
    if (def) {
      for (const fld of def.fields) {
        switch (fld.key) {
          case 'institution':  initial[fld.key] = doc.institution; break;
          case 'accountLast4': initial[fld.key] = doc.accountLast4; break;
          case 'ownerType':    initial[fld.key] = doc.ownerType; break;
          case 'ownerName':    initial[fld.key] = doc.ownerName; break;
          case 'entityName':   initial[fld.key] = doc.ownerName; break;
          case 'docDate':      initial[fld.key] = doc.documentDate; break;
          case 'taxYear':      initial[fld.key] = doc.taxYear; break;
          case 'lenderName':   initial[fld.key] = doc.lenderName; break;
          case 'expiresOn':    initial[fld.key] = doc.expiresOn; break;
          case 'loanKind': {
            const m = String(doc.documentName || '').match(/Loan Stmt - ([A-Za-z]+) -/);
            initial[fld.key] = m ? m[1] : '';
            break;
          }
          default:             initial[fld.key] = '';
        }
      }
    }
    return initial;
  };

  const [category, setCategory] = useState((DOC_TYPES[doc.docType] || {}).prefix || '');
  const [docTypeKey, setDocTypeKey] = useState(doc.docType || '');
  const docTypeDef = docTypeKey ? DOC_TYPES[docTypeKey] : null;
  const typeChanged = docTypeKey !== doc.docType;

  const [fields, setFields] = useState(initFieldsFor(doc.docType));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);

  const handleSave = async () => {
    if (savingRef.current) return;
    if (!docTypeKey || !docTypeDef) { setError('Pick a document type.'); return; }
    for (const fld of docTypeDef.fields) {
      if (fld.required) {
        const v = fields[fld.key];
        if (v === undefined || v === null || String(v).trim() === '') {
          setError(`Please fill in "${fld.label}".`);
          return;
        }
      }
    }
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const res = await authFetch('/.netlify/functions/update-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: doc.id,
          action: 'edit',
          fields: { ...fields, ...(fields.ownerName ? { ownerType: deriveOwnerType(fields.ownerName) } : {}), docTypeKey },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      onSuccess();
    } catch (e) {
      setError(e.message);
      setSaving(false);
      savingRef.current = false;
    }
  };

  return (
    <div style={s.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div style={s.modal}>
        <div style={s.modalHead}>
          <div style={s.modalTitle}>EDIT DOCUMENT METADATA</div>
          <button style={s.modalClose} onClick={onClose} disabled={saving}>×</button>
        </div>
        <div style={s.modalBody}>
          <div style={s.subHint}>
            Editing metadata will rebuild the filename and rename the file in Drive. The link stays the same.
          </div>

          <div style={s.field}>
            <div style={s.fieldLabel}>Category</div>
            <select style={s.select} value={category} disabled={saving}
              onChange={(e) => { setCategory(e.target.value); setDocTypeKey(''); setFields({}); }}>
              <option value="">Select…</option>
              {CATEGORIES.filter(x => x.prefix !== 'X').map(x => <option key={x.prefix} value={x.prefix}>{x.label}</option>)}
            </select>
          </div>
          <div style={{ ...s.field, marginTop:'12px' }}>
            <div style={s.fieldLabel}>Document type</div>
            <select style={s.select} value={docTypeKey} disabled={saving}
              onChange={(e) => { const k = e.target.value; setDocTypeKey(k); setFields(initFieldsFor(k)); }}>
              <option value="">Select…</option>
              {Object.entries(DOC_TYPES).filter(([, d]) => d.prefix === category).map(([k]) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          {typeChanged && docTypeKey && (
            <div style={{ fontSize:'11.5px', color:'#8a5a12', background:'#FAEEDA', border:'1px solid #EED9B4', borderRadius:'8px', padding:'7px 11px', marginTop:'10px' }}>
              Changing the type renames the file in Drive and re-files it in the portal. The Drive link stays the same.
            </div>
          )}

          {docTypeDef && (
            <div style={{ marginTop:'12px' }}>
              <div style={s.subLabel}>Metadata</div>
              <div style={s.fieldGrid}>
                {docTypeDef.fields.map(fld => (
                  <DynamicField key={fld.key} field={fld} value={fields[fld.key] || ''}
                    onChange={(v) => setFields(p => ({ ...p, [fld.key]: v }))}
                    disabled={saving}/>
                ))}
              </div>
            </div>
          )}

          {docTypeKey && (
            <div style={{ marginTop:'10px' }}>
              <div style={s.subLabel}>Filename preview</div>
              <div style={s.filenamePreview}>{buildPreviewFilename(docTypeKey, fields, (String(doc.documentName || '').split('.').pop() || 'pdf').toLowerCase(), null)}</div>
            </div>
          )}

          {error && <div style={s.errorMsg}>{error}</div>}

          <div style={s.modalActions}>
            <button style={s.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
            <button style={s.primaryBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ DYNAMIC FIELD ============

function DynamicField({ field, value, onChange, disabled }) {
  const label = (
    <div style={s.fieldLabel}>
      {field.label}{field.required && <span style={s.fieldReq}>*</span>}
    </div>
  );

  if (field.type === 'select') {
    return (
      <div style={s.field}>
        {label}
        <select style={s.select} value={value || ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {(field.options || []).map(o => <option key={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === 'date') {
    return (
      <div style={s.field}>
        {label}
        <input style={s.input} type="date" value={value || ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value)}/>
      </div>
    );
  }
  if (field.type === 'year') {
    return (
      <div style={s.field}>
        {label}
        <input style={s.input} type="number" min="1900" max="2100" value={value || ''} disabled={disabled}
          placeholder="2024"
          onChange={(e) => onChange(e.target.value)}/>
      </div>
    );
  }
  if (field.type === 'number') {
    return (
      <div style={s.field}>
        {label}
        <input style={s.input} type="text" inputMode="numeric" value={value || ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}/>
      </div>
    );
  }
  return (
    <div style={s.field}>
      {label}
      <input style={s.input} type="text" value={value || ''} disabled={disabled}
        onChange={(e) => onChange(e.target.value)}/>
    </div>
  );
}

// ============ DRIVE UPLOAD ============

function uploadToDriveAndGetFileMetadata({ sessionUri, file, mimeType, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sessionUri, true);
    xhr.setRequestHeader('Content-Type', mimeType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (!data.id) {
            reject(new Error('Drive upload succeeded but no file ID in response.'));
            return;
          }
          resolve(data);
        } catch (e) {
          reject(new Error('Drive upload succeeded but response body was not valid JSON: ' + e.message));
        }
      } else {
        reject(new Error(`Drive upload failed: ${xhr.status} ${xhr.responseText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during Drive upload'));
    xhr.send(file);
  });
}

// Background extraction support: the extract-document endpoint now returns
// immediately and the real work runs in extract-document-background, which
// writes the result to the Documents record. This polls that record's status
// until it settles, so the UI updates when the AI finishes.
const EXTRACTION_DONE = ['Reviewed - flagged', 'Reviewed - clean', 'Failed', 'Not applicable'];

async function pollExtractionStatus(documentId, { tries = 60, delayMs = 2500 } = {}) {
  for (let i = 0; i < tries; i++) {
    await new Promise((r) => setTimeout(r, delayMs));
    try {
      const res = await authFetch(`/.netlify/functions/get-document-extraction?documentId=${documentId}`);
      const d = await res.json();
      if (res.ok && d && EXTRACTION_DONE.includes(d.aiReviewStatus)) return d;
    } catch {}
  }
  return null;
}
